import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";

const ROLES = [
  { email: "admin@bsp.gov.ph", password: "admin123", label: "Admin" },
  { email: "sustainability@bsp.gov.ph", password: "sust123", label: "Sustainability" },
  { email: "production@bsp.gov.ph", password: "prod123", label: "Production" },
  { email: "procurement@bsp.gov.ph", password: "proc123", label: "Procurement" },
  { email: "auditor@bsp.gov.ph", password: "audit123", label: "Auditor" },
  { email: "supplier@denarii.com", password: "supp123", label: "Supplier" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@bsp.gov.ph");
  const [password, setPassword] = useState("admin123");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome to BSP PCF Platform");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left: form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="overline text-emerald-800">Bangko Sentral ng Pilipinas</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 mt-3">
            Currency PCF<br/>Platform
          </h1>
          <p className="mt-3 text-sm text-slate-600 max-w-sm">
            ISO 14067-aligned, AI-powered carbon footprint intelligence for Philippine currency production.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5">
            <div>
              <label className="overline block text-slate-500 mb-2">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 focus:border-emerald-700 outline-none text-sm font-mono bg-white"
                placeholder="you@bsp.gov.ph"
                required
              />
            </div>
            <div>
              <label className="overline block text-slate-500 mb-2">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 focus:border-emerald-700 outline-none text-sm font-mono bg-white"
                required
              />
            </div>
            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 py-3.5 bg-emerald-800 text-white font-semibold uppercase tracking-[0.2em] text-xs hover:bg-emerald-900 transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Enter Platform <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <div className="mt-10 pt-6 border-t border-slate-200">
            <div className="overline text-slate-500 mb-3">Demo accounts — click to autofill</div>
            <div className="grid grid-cols-2 gap-2">
              {ROLES.map((r) => (
                <button
                  key={r.email}
                  data-testid={`demo-${r.label.toLowerCase()}-btn`}
                  type="button"
                  onClick={() => { setEmail(r.email); setPassword(r.password); }}
                  className="px-3 py-2 text-xs border border-slate-300 hover:bg-slate-900 hover:text-white transition-colors text-left"
                >
                  <div className="font-semibold">{r.label}</div>
                  <div className="text-[10px] opacity-70 truncate font-mono">{r.email}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right: visual */}
      <div className="hidden lg:block relative bg-grid">
        <img
          src="https://images.unsplash.com/photo-1564143806867-42419ed0b58d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBiYW5rJTIwYnVpbGRpbmclMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc3Mzg5MTAwfDA&ixlib=rb-4.1.0&q=85"
          alt="Modern central bank architecture"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-950/30 via-emerald-900/10 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="overline text-amber-200">Verified · Audit-Ready · ISO Aligned</div>
          <div className="font-heading text-3xl font-bold mt-2 max-w-md leading-tight">
            From general ESG claims to <span className="text-amber-200">batch-level evidence</span>.
          </div>
        </div>
      </div>
    </div>
  );
}
